import { apiClient } from "./client";
import type {
  FormField,
  FormFieldCreate,
  FormFieldUpdate,
  FormFieldsPaginated,
  FormFieldGroup,
  FormFieldGroupCreate,
  FormFieldGroupUpdate,
  FormFieldGroupWithFields,
  FormFieldGroupsPaginated,
  MessageResponse,
} from "@/types";

// ============== Form Field Groups ==============

interface ListFormFieldGroupsParams {
  page?: number;
  page_size?: number;
}

export async function listFormFieldGroups(
  versionId: number,
  params: ListFormFieldGroupsParams = {}
): Promise<FormFieldGroupsPaginated> {
  const response = await apiClient.get<FormFieldGroupsPaginated>(
    `/simulators/versions/${versionId}/form-field-groups`,
    { params }
  );
  return response.data;
}

export async function listAllFormFieldGroups(
  versionId: number
): Promise<FormFieldGroupWithFields[]> {
  const response = await apiClient.get<FormFieldGroupWithFields[]>(
    `/simulators/versions/${versionId}/form-field-groups/all`
  );
  return response.data;
}

export async function getFormFieldGroup(
  versionId: number,
  groupId: number
): Promise<FormFieldGroupWithFields> {
  const response = await apiClient.get<FormFieldGroupWithFields>(
    `/simulators/versions/${versionId}/form-field-groups/${groupId}`
  );
  return response.data;
}

export async function createFormFieldGroup(
  versionId: number,
  data: FormFieldGroupCreate
): Promise<FormFieldGroup> {
  const response = await apiClient.post<FormFieldGroup>(
    `/simulators/versions/${versionId}/form-field-groups`,
    data
  );
  return response.data;
}

export async function updateFormFieldGroup(
  versionId: number,
  groupId: number,
  data: FormFieldGroupUpdate
): Promise<FormFieldGroup> {
  const response = await apiClient.patch<FormFieldGroup>(
    `/simulators/versions/${versionId}/form-field-groups/${groupId}`,
    data
  );
  return response.data;
}

export async function deleteFormFieldGroup(
  versionId: number,
  groupId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/form-field-groups/${groupId}`
  );
  return response.data;
}

export async function reorderFormFieldGroups(
  versionId: number,
  groupOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/form-field-groups/reorder`,
    { group_orders: groupOrders }
  );
  return response.data;
}

// ============== Form Fields ==============

interface ListFormFieldsParams {
  page?: number;
  page_size?: number;
  group_id?: number;
}

export async function listFormFields(
  versionId: number,
  params: ListFormFieldsParams = {}
): Promise<FormFieldsPaginated> {
  const response = await apiClient.get<FormFieldsPaginated>(
    `/simulators/versions/${versionId}/form-fields`,
    { params }
  );
  return response.data;
}

export async function listAllFormFields(versionId: number): Promise<FormField[]> {
  const response = await apiClient.get<FormField[]>(
    `/simulators/versions/${versionId}/form-fields/all`
  );
  return response.data;
}

export async function getFormField(
  versionId: number,
  fieldId: number
): Promise<FormField> {
  const response = await apiClient.get<FormField>(
    `/simulators/versions/${versionId}/form-fields/${fieldId}`
  );
  return response.data;
}

export async function createFormField(
  versionId: number,
  data: FormFieldCreate
): Promise<FormField> {
  const response = await apiClient.post<FormField>(
    `/simulators/versions/${versionId}/form-fields`,
    data
  );
  return response.data;
}

export async function updateFormField(
  versionId: number,
  fieldId: number,
  data: FormFieldUpdate
): Promise<FormField> {
  const response = await apiClient.patch<FormField>(
    `/simulators/versions/${versionId}/form-fields/${fieldId}`,
    data
  );
  return response.data;
}

export async function deleteFormField(
  versionId: number,
  fieldId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/form-fields/${fieldId}`
  );
  return response.data;
}

export async function reorderFormFields(
  versionId: number,
  fieldOrders: { id: number; order: number }[]
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/versions/${versionId}/form-fields/reorder`,
    { field_orders: fieldOrders }
  );
  return response.data;
}

// ============== Slug Usage Check ==============

export interface SlugUsage {
  type: "field_visibility" | "group_visibility" | "formula";
  entity_type: string;
  entity_id: number;
  entity_name: string;
  description: string;
}

export interface SlugUsageResponse {
  slug: string;
  is_used: boolean;
  usages: SlugUsage[];
  can_edit_slug: boolean;
}

export async function checkFormFieldSlugUsage(
  versionId: number,
  fieldId: number
): Promise<SlugUsageResponse> {
  const response = await apiClient.get<SlugUsageResponse>(
    `/simulators/versions/${versionId}/form-fields/${fieldId}/check-slug-usage`
  );
  return response.data;
}

// ============== Version Validation ==============

import type { ValidationResult } from "@/types";

export async function validateVersionConfiguration(
  versionId: number
): Promise<ValidationResult> {
  const response = await apiClient.post<ValidationResult>(
    `/simulators/versions/${versionId}/validate`
  );
  return response.data;
}
